import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "@tanstack/react-router";
import { ListChecks, Plus, Trash2 } from "lucide-react";
import {
  fetchUserCollections,
  createUserCollection,
  deleteUserCollection,
  type UserCollectionSummary,
} from "../api/user-collections";
import { useAuth } from "../hooks/useAuth";
import { AtlasPanel } from "../components/AtlasPanel";
import { CollectionCard } from "../components/CollectionCard";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { Skel } from "../components/Skeleton";

const FREE_LIST_LIMIT = 1;

function CreateListDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");

  const mutation = useMutation({
    mutationFn: () => createUserCollection({ name, description: description || undefined }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["user-collections"] });
      onClose();
    },
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 380,
          width: "90%",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
          New list
        </h2>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20, lineHeight: 1.5 }}>
          Pick your own peaks, landmarks and trails to track together.
        </p>

        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Scottish islands"
            style={{
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              padding: "8px 12px",
              fontSize: 14,
              color: "var(--text-primary)",
              outline: "none",
              width: "100%",
              boxSizing: "border-box",
            }}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
            Description <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(optional)</span>
          </span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              padding: "8px 12px",
              fontSize: 14,
              color: "var(--text-primary)",
              outline: "none",
              width: "100%",
              boxSizing: "border-box",
            }}
          />
        </label>

        {mutation.isError && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", marginBottom: 12 }}>
            {errorMessage(mutation.error, "Failed to create list")}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!name.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Creating…" : "Create list"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ListCard({ list, onNavigate, onDelete }: {
  list: UserCollectionSummary;
  onNavigate: () => void;
  onDelete: () => void;
}) {
  return (
    <div style={{ position: "relative" }}>
      <CollectionCard
        title={list.name}
        type="Custom list"
        value={list.completedCount}
        max={list.itemCount}
        icon={<ListChecks size={26} />}
        onClick={onNavigate}
      />
      <button
        type="button"
        aria-label={`Delete ${list.name}`}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          width: 28,
          height: 28,
          display: "grid",
          placeItems: "center",
          borderRadius: "var(--radius-md)",
          background: "var(--surface-raised)",
          border: "1px solid var(--border-default)",
          color: "var(--text-muted)",
          cursor: "pointer",
        }}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export default function MyListsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["user-collections"],
    queryFn: fetchUserCollections,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUserCollection(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-collections"] }),
  });

  const lists = data?.collections ?? [];
  const now = new Date();
  const isPro = user?.plan === "pro" && (!user.planExpiresAt || new Date(user.planExpiresAt) > now);
  const atLimit = !isPro && lists.length >= FREE_LIST_LIMIT;

  return (
    <div
      className="r-page"
      style={{
        padding: 28,
        overflow: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h1 style={{ font: "var(--type-display)", fontSize: 32, color: "var(--text-primary)", margin: "0 0 6px" }}>
            My lists
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0 }}>
            Your own collections, built from any peak, landmark or trail in Atlas.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {!isPro && (
            <Link to="/plan" style={{ textDecoration: "none" }}>
              <Badge variant="gold" dot>
                {lists.length} / {FREE_LIST_LIMIT} free list{FREE_LIST_LIMIT === 1 ? "" : "s"} · upgrade for unlimited
              </Badge>
            </Link>
          )}
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            disabled={atLimit}
            onClick={() => setDialogOpen(true)}
          >
            New list
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skel key={i} height="88px" />
          ))}
        </div>
      ) : lists.length === 0 ? (
        <AtlasPanel>
          <EmptyState
            icon={<ListChecks size={20} />}
            title="No lists yet"
            hint="Build your own goal — pick any peaks, landmarks or trails and Atlas tracks your progress the same way it does for curated collections."
            action={{ label: "New list", onClick: () => setDialogOpen(true) }}
          />
        </AtlasPanel>
      ) : (
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {lists.map((list) => (
            <ListCard
              key={list.id}
              list={list}
              onNavigate={() => void navigate({ to: "/lists/$id", params: { id: list.id } })}
              onDelete={() => {
                if (window.confirm(`Delete "${list.name}"? This can't be undone.`)) {
                  deleteMutation.mutate(list.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {dialogOpen && <CreateListDialog onClose={() => setDialogOpen(false)} />}
    </div>
  );
}
